import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, IconBadge, GradientButton, CTABanner, StepChip } from '../../components/common/Primitives';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import VideoLightbox from '../../components/common/VideoLightbox';
import { SCHOOL_EVIDENCE } from '../../lib/mockSchool';
import { youtubeThumb } from '../../lib/moduleVideos';
import { Check, PlayCircle, Image as ImageIcon } from 'lucide-react';

const FIELD_TRIAL_VIDEO_ID = 'TFD2Yv4fzbM';

export default function SchoolEvidence() {
  const d = SCHOOL_EVIDENCE;
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <PageShell>
      {/* HERO — with small right-side field-trial media placeholder (like How It Works) */}
      <section className="px-6 pt-12 pb-8" data-testid="school-evidence-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <h1 className="text-[36px] md:text-[52px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight /></div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-gradient-to-br from-purple-50 via-white to-mint-50 p-6 shadow-soft" data-testid="evidence-hero-media">
              <button type="button" onClick={() => setVideoOpen(true)} data-testid="school-evidence-hero-video-play" aria-label="Play field-trial video" className="group relative block w-full aspect-video rounded-2xl overflow-hidden focus:outline-none focus:ring-4 focus:ring-purple/30">
                <img src={youtubeThumb(FIELD_TRIAL_VIDEO_ID, 'maxresdefault')} alt="Field-trial media" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-black/35 group-hover:bg-black/25 transition-colors" />
                <span className="absolute inset-0 grid place-items-center"><span className="grid h-14 w-14 place-items-center rounded-full bg-white/95 shadow-soft group-hover:scale-105 transition-transform"><PlayCircle className="h-7 w-7 text-navy" /></span></span>
              </button>
              <div className="mt-3 text-[10.5px] font-bold tracking-widest text-purple">{d.status.mediaLabel}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CURRENT STATUS */}
      <section className="px-6 py-14" data-testid="evidence-current-status">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.status.eyebrow} title={d.status.title} desc={d.status.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {d.status.cards.map((c, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-card">
                <IconBadge icon={c.icon} color={c.color} size="lg" />
                <h3 className="mt-4 text-[16px] font-bold text-navy">{c.title}</h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-ink-500">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVALUATION JOURNEY */}
      <section className="px-6 py-14 bg-white" data-testid="evidence-evaluation-journey">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.journey.eyebrow} title={d.journey.title} desc={d.journey.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {d.journey.steps.map((s) => (
              <div key={s.n} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-card" data-testid={`evidence-step-${s.n}`}>
                <div className="flex items-center gap-3">
                  <IconBadge icon={s.icon} color={s.color} size="lg" />
                  <StepChip n={s.n} color={s.color} />
                </div>
                <h3 className="mt-4 text-[15.5px] font-bold text-navy">{s.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRELIMINARY VIEW */}
      <section className="px-6 py-14" data-testid="evidence-preliminary">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.preliminary.eyebrow} title={d.preliminary.title} desc={d.preliminary.desc} />

          {/* HEADLINE STATS STRIP */}
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="evidence-stats">
            {d.preliminary.stats.map((s, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-4 shadow-soft">
                <div className={`text-[22px] font-extrabold leading-none ${['text-purple','text-royal','text-cyan','text-mint','text-purple'][i]}`}>{s.k}</div>
                <div className="mt-2 text-[12px] font-bold text-navy leading-tight">{s.label}</div>
                <div className="mt-1 text-[10.5px] leading-snug text-ink-500">{s.note}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-[1.15fr_1fr] md:items-stretch">
            <div className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
              <div className="text-[14.5px] font-bold text-navy">{d.preliminary.summaryTitle}</div>
              <ul className="mt-3 space-y-2.5">
                {d.preliminary.summaryBullets.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] leading-relaxed text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint flex-none" /> {r}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white shadow-soft overflow-hidden self-start">
              <button type="button" onClick={() => setVideoOpen(true)} data-testid="school-evidence-field-trial-play" aria-label="Play field-trial video" className="group relative block w-full aspect-[16/10] overflow-hidden focus:outline-none focus:ring-4 focus:ring-purple/30">
                <img src={youtubeThumb(FIELD_TRIAL_VIDEO_ID, 'hqdefault')} alt="Field-trial preview" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-black/25 group-hover:bg-black/15 transition-colors" />
                <span className="absolute inset-0 grid place-items-center"><span className="grid h-11 w-11 place-items-center rounded-full bg-white/95 shadow-soft"><PlayCircle className="h-5 w-5 text-navy" /></span></span>
              </button>
              <div className="px-3 py-2 text-[10.5px] font-bold tracking-widest text-purple">{d.preliminary.mediaLabel}</div>
            </div>
          </div>

          {/* QUALITATIVE THEMES */}
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-[1.15fr_1fr]">
            <div className="rounded-2xl border border-canvas-200 bg-canvas-100 p-6 shadow-soft" data-testid="evidence-themes">
              <div className="text-[11.5px] font-bold tracking-widest text-royal">QUALITATIVE THEMES</div>
              <div className="mt-1 text-[16px] font-bold text-navy">{d.preliminary.themesTitle}</div>
              <ul className="mt-4 space-y-2.5">
                {d.preliminary.themes.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] leading-relaxed text-ink-700"><span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-purple" /> {t}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft" data-testid="evidence-voice">
              <div className="text-[11.5px] font-bold tracking-widest text-mint">STUDENT VOICE</div>
              <div className="mt-1 text-[16px] font-bold text-navy">What students told us</div>
              <ul className="mt-4 space-y-4">
                {d.preliminary.voice.map((q, i) => (
                  <li key={i} className="border-l-2 border-mint pl-3 text-[13.5px] italic text-ink-700 leading-relaxed">“{q}”</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {videoOpen && <VideoLightbox videoId={FIELD_TRIAL_VIDEO_ID} title="Field trial — students using Gyan Dhara" onClose={() => setVideoOpen(false)} />}

      {/* RESEARCH GOVERNANCE */}
      <section className="px-6 py-14 bg-white" data-testid="evidence-governance">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.governance.eyebrow} title={d.governance.title} desc={d.governance.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {d.governance.principles.map((p, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
                <IconBadge icon={p.icon} color={p.color} size="lg" />
                <h3 className="mt-4 text-[15.5px] font-bold text-navy">{p.title}</h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-ink-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={d.cta.title} desc={d.cta.desc} primary={d.cta.primary} secondary={d.cta.secondary} />
        </div>
      </section>
    </PageShell>
  );
}
